import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Badge, Card, Spinner, Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import UserContext from "../../context/UserContext";

const PaymentApproval = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  const PAGE_SIZE = 10;
  
  // States cho bộ lọc
  const [requesterFilter, setRequesterFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");

  const getUserId = () => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.id || user?.userId || storedUser?.id || storedUser?.userId;
  };

  useEffect(() => {
    fetchPendingPayments();
  }, [user]);

  const fetchPendingPayments = async () => {
    setLoading(true);
    setCurrentPage(1);
    try {
      const currentId = getUserId();
      if (!currentId) return;

      // Truyền cả requesterName và title vào hàm endpoint
      const res = await axiosClient.get(
        API_ENDPOINTS.paymentNotifications(currentId, { 
          requesterName: requesterFilter,
          title: titleFilter 
        })
      );
      
      setPendingPayments(res.data || []);
    } catch (err) {
      console.error("Error loading Payment approvals:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "VND") => {
    const numericAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    }).format(numericAmount);
  };

  const indexOfLastItem = currentPage * PAGE_SIZE;
  const indexOfFirstItem = indexOfLastItem - PAGE_SIZE;
  const currentItems = pendingPayments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(pendingPayments.length / PAGE_SIZE);

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-dark text-uppercase mb-0">Payment Approval Portal</h3>
          <p className="text-muted small mb-0">Review and authorize payment requests</p>
        </div>
        <Button variant="primary" size="sm" onClick={fetchPendingPayments} disabled={loading}>
          <i className={`fas fa-sync-alt me-1 ${loading ? 'fa-spin' : ''}`}></i> Refresh
        </Button>
      </div>

      {/* Khu vực lọc */}
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <Row className="align-items-center g-2">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Filter by Title..."
                value={titleFilter}
                onChange={(e) => setTitleFilter(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Filter by Requester Name..."
                value={requesterFilter}
                onChange={(e) => setRequesterFilter(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Button variant="secondary" className="w-100" onClick={fetchPendingPayments}>
                Apply Filter
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
        <Card.Body className="p-0">
          <Table hover responsive className="align-middle mb-0">
            <thead style={{ backgroundColor: "#f1f4f9" }}>
              <tr>
                <th className="ps-4 py-3 text-muted fw-bold small text-uppercase">Payment Code</th>
                <th className="py-3 text-muted fw-bold small text-uppercase">Title</th>
                <th className="py-3 text-muted fw-bold small text-uppercase">Requester</th>
                <th className="py-3 text-muted fw-bold small text-uppercase text-end">Amount</th>
                <th className="py-3 text-muted fw-bold small text-uppercase text-center">Status</th>
                <th className="py-3 text-muted fw-bold small text-end pe-4 text-uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr key={item.id} className="border-bottom border-light">
                    <td className="ps-4 py-3 fw-bold text-secondary">{item.paymentCode || "N/A"}</td>
                    <td className="py-3">
                      <div className="fw-bold text-dark">{item.title}</div>
                      <small className="text-muted">Ref: #{item.paymentRequestId}</small>
                    </td>
                    <td className="py-3 fw-bold">{item.senderName || "System"}</td>
                    <td className="py-3 text-end fw-bold text-primary">{formatCurrency(item.amount, item.currency)}</td>
                    <td className="text-center">
                      <Badge className={`px-3 py-2 rounded-pill fw-normal ${item.color || 'bg-secondary'}`}>
                        <i className={`fas ${item.icon || 'fa-info-circle'} me-1`}></i> 
                        {item.status ? item.status.toUpperCase() : "UNKNOWN"}
                      </Badge>
                    </td>
                    <td className="text-end pe-4">
                      <Button variant="outline-primary" size="sm" className="rounded-pill px-4" 
                        onClick={() => navigate(`/payment-approval-detail/${item.paymentRequestId}`)}>
                        {item.status === "Pending" ? "Process Now" : "View"}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">No records found.</td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Pagination mới đồng bộ với PaymentAdminManagement */}
          {totalPages > 1 && (
            <div className="card-footer bg-white border-0 py-3 text-center">
              <button 
                className="btn btn-sm btn-light mx-1 border shadow-sm rounded-circle" 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="mx-3 fw-bold text-primary small">Page {currentPage} of {totalPages}</span>
              <button 
                className="btn btn-sm btn-light mx-1 border shadow-sm rounded-circle" 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaymentApproval;